import { Box, Heading, Text } from "@chakra-ui/react";
import {
  ImportStatus,
  DataSourceCode,
  DataSourceType,
} from "@syla/shared/types/models/DataSourceBase";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import React from "react";
import { DataSourceInputType } from "../../../../helper/dataSource/DataSourceInputType";
import { ImportMethod } from "../panels/ImportMethodSelectionPanel";

export function ImportMethodHeading({
  dataSource,
  method,
}: {
  dataSource: DataSourceWithNetworks;
  method: ImportMethod;
}): React.ReactElement | null {
  const data = getHeadingAndText(dataSource, method);

  if (!data) {
    return null;
  }

  return (
    <Box w="full">
      <Heading as="h2" size="md">
        {data.heading}
      </Heading>
      <Text>{data.text}</Text>
    </Box>
  );
}

function getHeadingAndText(
  dataSource: DataSourceWithNetworks,
  method: ImportMethod,
): { heading: string; text: string } | null {
  if (method === DataSourceInputType.Api) {
    if (dataSource.syncAuthType === "oauth") {
      return {
        heading: "Connect and sync",
        text: `Connect to ${dataSource.name} to sync your transactions.`,
      };
    }

    if (dataSource.type === DataSourceType.Exchange) {
      return {
        heading: "API sync",
        text: `Create an API key in ${dataSource.name} to sync your transactions.`,
      };
    }

    if (dataSource.type === DataSourceType.Wallet) {
      return {
        heading: "Wallet sync",
        text: "Enter your wallet details to sync transactions directly from the blockchain.",
      };
    }

    if (dataSource.type === DataSourceType.Blockchain) {
      return {
        heading: "Blockchain sync",
        text: `Enter your wallet address to sync transactions directly from ${dataSource.name}.`,
      };
    }
  }

  if (method === DataSourceInputType.Csv) {
    if (dataSource.fileImportStatus === ImportStatus.Alpha) {
      return null;
    }

    if (dataSource.code === DataSourceCode.OpeningBalances) {
      return {
        heading: "File import",
        text: "Complete the Opening Balances template, then upload it below.",
      };
    }

    if (
      dataSource.type === DataSourceType.Exchange ||
      dataSource.type === DataSourceType.Wallet ||
      dataSource.type === DataSourceType.Import
    ) {
      return {
        heading: "File import",
        text: `Download your transaction history from ${dataSource.name}, then upload it below.`,
      };
    }

    if (dataSource.type === DataSourceType.Blockchain) {
      return {
        heading: "File import",
        text: `Download your transaction history, then upload it below.`,
      };
    }
  }

  if (method === DataSourceInputType.Custom) {
    return {
      heading: "Manual import",
      text: "Complete the Custom Ledgers template, then upload it below.",
    };
  }

  if (method === "Empty") {
    return {
      heading: "Create empty data source",
      text: `Create an empty data source and import your transactions later.`,
    };
  }

  return null;
}
